import type { LDFlagSet, LDSingleKindContext } from '@launchdarkly/react-native-client-sdk';
import { defaultsDeep, merge, omit } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { getDeviceId } from 'utils/device-id';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

import * as c from './constants';
import { type AllFlagsType, flagDefinitions } from './flags';

/**
 * Returns a stable device identifier for LaunchDarkly user context creation.
 * Checks the cached user context for an existing device id first, otherwise
 * falls back to the current device id or generates a new UUID.
 */
const getUserDeviceId = async (): Promise<string> => {
  const cachedUserContext = normalizeUserAttributes(
    LocalStorage.getItem(StorageKeys.LAUNCH_DARKLY_USER_ATTRIBUTES) ?? {}
  );
  const cachedDeviceId = cachedUserContext?.device_id ?? cachedUserContext?.custom?.device_id;

  if (cachedDeviceId) {
    return cachedDeviceId as string;
  }

  return (await getDeviceId()) || uuidv4();
};

/**
 * Recursively trims whitespace from all string values in an attributes object.
 */
const trimStringValues = <T>(attributes: T): T => {
  if (typeof attributes !== 'object' || attributes == null) {
    return attributes;
  }

  return Object.entries(attributes).reduce((acc, [key, value]) => {
    switch (typeof value) {
      case 'object': {
        acc[key] = trimStringValues(value);
        break;
      }

      case 'string': {
        acc[key] = value.trim();
        break;
      }

      default: {
        acc[key] = value;
      }
    }

    return acc;
  }, {} as T);
};

/**
 * Normalizes user attributes for LaunchDarkly by trimming strings and
 * lowercasing the email field when present.
 */
const normalizeUserAttributes = (
  userAttributes: Partial<LDSingleKindContext>
): Partial<LDSingleKindContext> => {
  const result = trimStringValues(userAttributes);

  if ('email' in result && typeof result.email === 'string') {
    result.email = result.email.toLowerCase();
  }

  // omit privateAttributes and _meta because they are or contain arrays that were serialized to objects
  // We do not need them since we moved privateAttributes to the client level instead of user context.
  return omit(result, ['privateAttributes', '_meta']);
};

/**
 * Builds the base LaunchDarkly user context for the current device.
 */
const createBaseUserAttributes = ({
  deviceId,
  anonymous = false,
}: {
  deviceId: string;
  anonymous?: boolean;
}): LDSingleKindContext => {
  return normalizeUserAttributes({
    kind: 'user',
    key: deviceId,
    anonymous,
    host: c.host,
    platform: c.platform,
    mobileOS: c.mobileOS,
    device_id: deviceId,
    commit: c.commit,
    userClient: c.userClient,
    appVersion: c.appVersion,
    appShellVersion: c.appShellVersion,
    language: c.language,
    sanityDataset: c.sanityDataset,
    country: c.country,
    custom: {
      // Duplicate property because the previous implementation had it at root as well as nested in custom and it's not clear if
      // any flags target the custom attribute
      sanityDataset: c.sanityDataset,
    },
  }) as LDSingleKindContext;
};

/**
 * After `defaultsDeep(cached, base)`, the cache still wins on defined keys (e.g. stale appVersion /
 * appShellVersion after OTA or store upgrades). Reapply build/runtime fields from `base` while
 * keeping identity and custom targeting attributes from the merge.
 */
const applyCurrentRuntimeUserAttributes = (
  merged: LDSingleKindContext,
  base: LDSingleKindContext
): LDSingleKindContext => {
  return normalizeUserAttributes({
    ...merged,
    host: base.host,
    platform: base.platform,
    mobileOS: base.mobileOS,
    device_id: base.device_id,
    commit: base.commit,
    userClient: base.userClient,
    appVersion: base.appVersion,
    appShellVersion: base.appShellVersion,
    language: base.language,
    sanityDataset: base.sanityDataset,
    country: base.country,
    custom: {
      ...(merged.custom ?? {}),
      ...base.custom,
    },
  }) as LDSingleKindContext;
};

/**
 * Returns the LaunchDarkly user context: cached identity and custom attributes, with current
 * build/runtime fields from the running app.
 */
export const getUserContext = async (): Promise<LDSingleKindContext> => {
  const deviceId = await getUserDeviceId();
  const cachedUserContext = LocalStorage.getItem(StorageKeys.LAUNCH_DARKLY_USER_ATTRIBUTES);

  let initialUserContext: LDSingleKindContext;
  if (cachedUserContext && cachedUserContext?.key) {
    const base = createBaseUserAttributes({ deviceId });
    const merged = defaultsDeep(
      normalizeUserAttributes(cachedUserContext),
      base
    ) as LDSingleKindContext;
    initialUserContext = applyCurrentRuntimeUserAttributes(merged, base);
  } else {
    initialUserContext = createBaseUserAttributes({ deviceId, anonymous: true });
  }

  LocalStorage.setItem(StorageKeys.LAUNCH_DARKLY_USER_ATTRIBUTES, initialUserContext);
  return initialUserContext;
};

/**
 * Get function that resets the cached LaunchDarkly user context to an anonymous device-based user,
 * updates storage, and re-identifies the client with the new context.
 */
export const getClearUserContext = (
  ldIdentify: (context: LDSingleKindContext) => Promise<void>
) => {
  return async (): Promise<LDSingleKindContext> => {
    const deviceId = await getUserDeviceId();

    const anonymousUserContext = createBaseUserAttributes({ deviceId, anonymous: true });
    LocalStorage.setItem(StorageKeys.LAUNCH_DARKLY_USER_ATTRIBUTES, anonymousUserContext);

    await ldIdentify(anonymousUserContext);

    return anonymousUserContext;
  };
};

/**
 * Gets function that merges updated attributes into the cached LaunchDarkly user context,
 * persists the result, and re-identifies the client with the new context.
 */
export const getUpdateUserContext = (
  ldIdentify: (context: LDSingleKindContext) => Promise<void>
) => {
  return async (changedAttributes: Partial<LDSingleKindContext>): Promise<LDSingleKindContext> => {
    const normalizedChangedAttributes = normalizeUserAttributes(changedAttributes);
    const currentUserContext = await getUserContext();

    const updatedUserContext = merge({}, currentUserContext, normalizedChangedAttributes);
    LocalStorage.setItem(StorageKeys.LAUNCH_DARKLY_USER_ATTRIBUTES, updatedUserContext);

    await ldIdentify(updatedUserContext);

    return updatedUserContext;
  };
};

export const getResolvedAllFlags = (flagSet: LDFlagSet) => {
  const allFlagsWithFallbacks = {} as AllFlagsType;

  for (const definitionKey in flagDefinitions) {
    const definition = flagDefinitions[definitionKey as keyof typeof flagDefinitions];
    allFlagsWithFallbacks[definitionKey] = flagSet?.[definition.key] ?? definition.defaultValue;
  }

  return allFlagsWithFallbacks;
};
